import React, { useCallback, useRef, useState } from 'react';
import { File, Loader2, Upload, X } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import { cn } from '~/shadcn/lib/utils';

/**
 * Drag & drop file upload zone for bulk uploads.
 *
 * @param {Object} props
 * @param {Function} props.onFileSelect - Called when a file is selected
 * @param {boolean} [props.disabled] - Disable the dropzone
 * @param {boolean} [props.isUploading] - Show uploading state
 * @param {string} [props.accept] - Accepted file types (default: '.csv')
 * @param {string} [props.className] - Additional classes
 */
export function BulkUploadDropzone({
    accept = '.csv',
    className,
    disabled = false,
    isUploading = false,
    onFileSelect,
}) {
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const isDisabled = disabled || isUploading;

    /**
     * Validates if a file has an accepted extension
     */
    const isValidFileExtension = useCallback(
        (file) => {
            if (!file) return false;
            const extension = file.name.split('.').pop()?.toLowerCase();
            const acceptedExtensions = accept.split(',').map((ext) => ext.trim().replace('.', ''));
            return acceptedExtensions.includes(extension);
        },
        [accept],
    );

    const handleFileChange = useCallback(
        (file) => {
            if (!file || isDisabled) return;

            setSelectedFile(file);
            onFileSelect?.(file);
        },
        [onFileSelect, isDisabled],
    );

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file && isValidFileExtension(file)) {
            handleFileChange(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDisabled) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (isDisabled) return;

        const file = e.dataTransfer.files?.[0];
        if (file && isValidFileExtension(file)) {
            handleFileChange(file);
        }
    };

    const handleClick = () => {
        if (!isDisabled) {
            inputRef.current?.click();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    const handleClearFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
            onFileSelect?.(null);
        }
    };

    return (
        <div className={cn('w-full', className)}>
            <div
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                    'relative flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-8 transition-colors',
                    'cursor-pointer hover:border-primary/50 hover:bg-muted/50',
                    isDragOver && 'border-primary bg-primary/5',
                    isDisabled && 'cursor-not-allowed opacity-50',
                    selectedFile && 'border-solid border-primary/30 bg-primary/5',
                )}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    disabled={isDisabled}
                    className="hidden"
                />

                {isUploading && (
                    <>
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <div className="text-center">
                            <p className="text-sm font-medium">Uploading...</p>
                            <p className="text-xs text-muted-foreground">{selectedFile?.name}</p>
                        </div>
                    </>
                )}

                {!isUploading && selectedFile && (
                    <>
                        <File className="h-10 w-10 text-primary" />
                        <div className="text-center">
                            <p className="text-sm font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleClearFile} className="absolute right-2 top-2">
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove file</span>
                        </Button>
                    </>
                )}

                {!isUploading && !selectedFile && (
                    <>
                        <Upload className="h-10 w-10 text-muted-foreground" />
                        <div className="text-center">
                            <p className="text-sm font-medium">
                                Drop your CSV file here, or <span className="text-primary">browse</span>
                            </p>
                            <p className="text-xs text-muted-foreground">Supports: {accept}</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
